import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material"
import { Employee } from "graphql/types"
import React, { useMemo } from "react"
import { A11Y_LABELS, ALL_EMPLOYEES_VALUE, ERROR_MESSAGES, LOADING_HEIGHT } from "./constants"

// Helper function for employee display names
const getEmployeeDisplayName = (employee: Employee): string => {
  if (employee.EmployeeName) {
    return employee.EmployeeName
  }

  const { FirstName, LastName } = employee.ContactInfo || {}
  if (FirstName && LastName) {
    return `${FirstName} ${LastName}`
  }

  if (FirstName) return FirstName
  if (LastName) return LastName

  return "Unbekannter Mitarbeiter"
}

// Employee Selector Component
interface EmployeeSelectorProps {
  employees: Employee[]
  selectedEmployeeId: string
  onEmployeeChange: (event: SelectChangeEvent<string>) => void
  currentUserId: string
}

export const EmployeeSelector = React.memo<EmployeeSelectorProps>(function EmployeeSelector({
  employees,
  selectedEmployeeId,
  onEmployeeChange,
  currentUserId,
}) {
  // Memoize current user display name for performance
  const getCurrentUserDisplayName = useMemo(() => {
    const currentEmployee = employees.find((emp) => emp.id === currentUserId)
    if (currentEmployee) {
      return getEmployeeDisplayName(currentEmployee)
    }
    return "Meine Projekte"
  }, [employees, currentUserId])

  // Memoize filtered employees list
  const otherEmployees = useMemo(
    () => employees.filter((employee) => employee.id !== currentUserId),
    [employees, currentUserId]
  )

  // Sort employees by display name (ascending)
  const sortedOtherEmployees = useMemo(
    () =>
      [...otherEmployees].sort((a, b) =>
        getEmployeeDisplayName(a).localeCompare(getEmployeeDisplayName(b), "de", {
          sensitivity: "base",
        })
      ),
    [otherEmployees]
  )

  return (
    <FormControl sx={{ minWidth: 200 }}>
      <InputLabel id="employee-select-label">{A11Y_LABELS.EMPLOYEE_SELECT}</InputLabel>
      <Select
        labelId="employee-select-label"
        id="employee-select"
        value={selectedEmployeeId}
        onChange={onEmployeeChange}
        label={A11Y_LABELS.EMPLOYEE_SELECT}
        aria-describedby="employee-select-help"
      >
        <MenuItem value={currentUserId}>{getCurrentUserDisplayName}</MenuItem>
        <MenuItem value={ALL_EMPLOYEES_VALUE}>Alle Mitarbeiter</MenuItem>
        {sortedOtherEmployees.map((employee) => (
          <MenuItem key={employee.id} value={employee.id}>
            {getEmployeeDisplayName(employee)}
          </MenuItem>
        ))}
      </Select>
      <Box
        component="span"
        sx={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
        id="employee-select-help"
      >
        {A11Y_LABELS.EMPLOYEE_SELECT_HELP}
      </Box>
    </FormControl>
  )
})

// Loading State Component
export const LoadingState = () => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: LOADING_HEIGHT,
      }}
      role="status"
      aria-label="Projekte werden geladen"
    >
      <CircularProgress aria-describedby="loading-message" />
      <Box
        component="span"
        sx={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
        id="loading-message"
      >
        Projekte werden geladen
      </Box>
    </Box>
  )
}

// Empty State Component
interface EmptyStateProps {
  selectedEmployeeId: string
  employees: Employee[]
  onEmployeeChange: (event: SelectChangeEvent<string>) => void
  currentUserId: string
}

export const EmptyState = React.memo<EmptyStateProps>(function EmptyState({
  selectedEmployeeId,
  employees,
  onEmployeeChange,
  currentUserId,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: LOADING_HEIGHT,
        gap: 2,
      }}
      role="region"
      aria-labelledby="empty-state-title"
      aria-describedby="empty-state-description"
    >
      <Typography id="empty-state-title" variant="h6" color="text.secondary">
        {A11Y_LABELS.EMPTY_STATE}
      </Typography>
      <Typography
        id="empty-state-description"
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "center", maxWidth: 400 }}
      >
        {selectedEmployeeId === ALL_EMPLOYEES_VALUE
          ? ERROR_MESSAGES.NO_PROJECTS_AVAILABLE
          : ERROR_MESSAGES.NO_ASSIGNED_PROJECTS}
      </Typography>
      <EmployeeSelector
        employees={employees}
        selectedEmployeeId={selectedEmployeeId}
        onEmployeeChange={onEmployeeChange}
        currentUserId={currentUserId}
      />
    </Box>
  )
})
